import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import VM from 'scratch-vm';

import {
    setSeedsWorkspaceId,
    setAutoSaveStatus
} from '../reducers/seeds-workspace';
import {setProjectUnchanged} from '../reducers/project-changed';
import {saveProjectToSEEDS} from './seeds-api';

const AUTO_SAVE_DELAY = 6000; // 6 seconds debounce

const SeedsAutoSaveHOC = function (WrappedComponent) {
    class SeedsAutoSaveComponent extends React.Component {
        constructor (props) {
            super(props);
            this.autoSaveTimeoutRef = null;
            this.isSaving = false;
        }

        componentDidUpdate (prevProps) {
            // Trigger auto-save when project changes
            if (
                this.props.projectChanged &&
                !prevProps.projectChanged &&
                !this.props.isReadOnly &&
                !this.isSaving
            ) {
                this.scheduleAutoSave();
            }
        }

        componentWillUnmount () {
            if (this.autoSaveTimeoutRef) {
                clearTimeout(this.autoSaveTimeoutRef);
            }
        }

        scheduleAutoSave () {
            // Clear any existing timeout
            if (this.autoSaveTimeoutRef) {
                clearTimeout(this.autoSaveTimeoutRef);
            }

            // Schedule new auto-save
            this.autoSaveTimeoutRef = setTimeout(() => {
                this.performAutoSave();
            }, AUTO_SAVE_DELAY);
        }

        performAutoSave () {
            if (this.isSaving || this.props.isReadOnly) {
                return;
            }

            this.isSaving = true;
            this.props.onSetAutoSaveStatus('saving');

            // Generate workspace ID if new
            const workspaceId =
                this.props.seedsWorkspaceId ||
                `workspace-${Date.now()}-${Math.random()
                    .toString(36)
                    .substr(2, 9)}`;

            const workspaceName = this.props.projectTitle || 'Untitled';

            // Get .sb3 blob from VM
            this.props.vm
                .saveProjectSb3()
                .then(sb3Blob =>
                    saveProjectToSEEDS(workspaceId, workspaceName, sb3Blob)
                )
                .then(() => {
                    // Update workspace ID if new
                    if (!this.props.seedsWorkspaceId) {
                        this.props.onSetSeedsWorkspaceId(workspaceId);

                        // Update URL without reload
                        const url = new URL(window.location);
                        url.searchParams.set('workspaceId', workspaceId);
                        window.history.pushState({}, '', url);
                    }

                    // Mark project as unchanged
                    this.props.onProjectUnchanged();

                    // Update status
                    this.props.onSetAutoSaveStatus('saved');

                    // Notify parent window
                    window.parent.postMessage(
                        {
                            type: 'WORKSPACE_SAVED',
                            workspaceId: workspaceId
                        },
                        process.env.SEEDS_URL || '*'
                    );

                    // Clear "saved" status after 2 seconds
                    setTimeout(() => {
                        this.props.onSetAutoSaveStatus('idle');
                    }, 2000);
                })
                .catch(err => {
                    // eslint-disable-next-line no-console
                    console.error('Auto-save error:', err);
                    this.props.onSetAutoSaveStatus('error');

                    // Clear error status after 3 seconds
                    setTimeout(() => {
                        this.props.onSetAutoSaveStatus('idle');
                    }, 3000);
                })
                .finally(() => {
                    this.isSaving = false;
                });
        }

        render () {
            const {
                /* eslint-disable no-unused-vars */
                projectChanged,
                seedsWorkspaceId,
                isReadOnly,
                onSetSeedsWorkspaceId,
                onSetAutoSaveStatus,
                onProjectUnchanged,
                vm,
                projectTitle,
                /* eslint-enable no-unused-vars */
                ...componentProps
            } = this.props;

            return <WrappedComponent {...componentProps} />;
        }
    }

    SeedsAutoSaveComponent.propTypes = {
        isReadOnly: PropTypes.bool,
        onProjectUnchanged: PropTypes.func,
        onSetAutoSaveStatus: PropTypes.func,
        onSetSeedsWorkspaceId: PropTypes.func,
        projectChanged: PropTypes.bool,
        projectTitle: PropTypes.string,
        seedsWorkspaceId: PropTypes.string,
        vm: PropTypes.instanceOf(VM)
    };

    const mapStateToProps = state => ({
        projectChanged: state.scratchGui.projectChanged,
        projectTitle: state.scratchGui.projectTitle,
        seedsWorkspaceId: state.scratchGui.seedsWorkspace.workspaceId,
        isReadOnly: state.scratchGui.seedsWorkspace.isReadOnly,
        vm: state.scratchGui.vm
    });

    const mapDispatchToProps = dispatch => ({
        onSetSeedsWorkspaceId: workspaceId =>
            dispatch(setSeedsWorkspaceId(workspaceId)),
        onSetAutoSaveStatus: status => dispatch(setAutoSaveStatus(status)),
        onProjectUnchanged: () => dispatch(setProjectUnchanged())
    });

    return connect(mapStateToProps, mapDispatchToProps)(SeedsAutoSaveComponent);
};

export {SeedsAutoSaveHOC as default};
